import { CloseOutlined, MenuOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { UserMenu } from '@components/molecules';
import { CartDrawer } from '@components/organisms';
import { useAuth, useCart } from '@hooks';
import { useCartDrawer } from '@contexts';
import { Badge, Skeleton } from 'antd';
import { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';

// Importando GSAP e o plugin ScrollTrigger
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Registrando o plugin
gsap.registerPlugin(ScrollTrigger);

export const PublicTemplate = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { profile: { data: profile, isLoading } } = useAuth();
  const isAuthenticated = !!profile;
  const { cart } = useCart(isAuthenticated);
  const { openDrawer } = useCartDrawer();

 // Refs para os elementos que vamos animar
 const headerRef = useRef<HTMLElement>(null);
 const logoRef = useRef<HTMLImageElement>(null);

  // Efeito para detectar scroll e ajustar o header (desktop e mobile)
  useEffect(() => {
    const ctx = gsap.context(() => {
      const header = headerRef.current;
      const logo = logoRef.current;
      
      if (!header || !logo) return;

      // Função para atualizar o estado do header baseado no scroll
      const updateHeaderState = (progress: number) => {
        // Toggle classe no header - CSS cuida das transições de cor
        if (progress > 0) {
          header.classList.add('header-scrolled');
        } else {
          header.classList.remove('header-scrolled');
        }
      };

      // Verificar estado inicial do scroll
      const initialScrollY = window.scrollY;
      const scrollRange = 500;
      const initialProgress = Math.min(initialScrollY / scrollRange, 1);
      updateHeaderState(initialProgress);

      // Animação da LOGO (preto para branco) sincronizada com o scroll
      // Usando GSAP para animação suave
      gsap.to(logo, {
        filter: 'invert(100%)',
        scrollTrigger: {
          trigger: 'body',
          start: 'top top',
          end: '+=500',
          scrub: 0.5,
        },
      });

      // Criar um ScrollTrigger único para controlar a classe do header
      ScrollTrigger.create({
        trigger: 'body',
        start: 'top top',
        end: '+=500',
        onUpdate: (self) => {
          updateHeaderState(self.progress);
        },
        onEnter: () => {
          updateHeaderState(1);
        },
        onLeaveBack: () => {
          updateHeaderState(0);
        },
      });
    });

    return () => ctx.revert();
  }, [mobileMenuOpen]);

  const handleMobileLinkClick = () => {
    setMobileMenuOpen(false);
  };
  
  // Componente reutilizável para os links de navegação
  const NavLinks = ({ isMobile = false }) => (
    <>
      <Link to="/about" className="header-nav-link" onClick={isMobile ? handleMobileLinkClick : undefined}>ABOUT</Link>
      <Link to="/merch" className="header-nav-link" onClick={isMobile ? handleMobileLinkClick : undefined}>CHAINS</Link>
      <Link to="/fabrication" className="header-nav-link" onClick={isMobile ? handleMobileLinkClick : undefined}>FABRICATION</Link>
      {profile?.profile === 'admin' && (
        <Link to="/merch" className="header-nav-link" onClick={isMobile ? handleMobileLinkClick : undefined}>SHOP</Link>
      )}
    </>
  );

  return (
    <div>
      <CartDrawer />
      <header 
        ref={headerRef}
        className="header-top bg-white fixed top-0 left-0 w-full z-20 transition-all duration-300 px-4 md:px-8 py-2"
      >
        <div className="container mx-auto">
          {/* Layout em duas colunas */}
          <div className="flex justify-between items-center">
            {/* Coluna Esquerda - Logo */}
            <div className="flex items-center gap-2 md:gap-4 cursor-pointer" onClick={() => { navigate('/'); window.scrollTo(0, 0); }}>
              <div className="w-8 md:w-10">
                <img src="/assets/images/ModlinkLogo.png" alt="Modlink Logo" />
              </div>
              <div className="w-24 md:w-32 lg:w-40">
                <img ref={logoRef} src="/assets/images/ModLinkWordmark.png" alt="Modlink Logo" />
              </div>
            </div>

            {/* Coluna Direita - Navbar e Login */}
            <div className="flex items-center gap-4 md:gap-6">
              {/* Navegação Desktop */}
              <nav className="hidden md:flex items-center font-orator font-bold text-xl lg:text-2xl space-x-4 lg:space-x-8 pr-4">
                <NavLinks />
              </nav>

              {/* Cart Icon */}
              {profile?.profile === 'admin' && (
                <div className="flex items-center">
                  <Badge 
                    count={cart?.totalItems || 0} 
                    showZero={false}
                    className="cursor-pointer"
                    onClick={openDrawer}
                    style={{ 
                      backgroundColor: '#F74902',
                    }}
                  >
                    <ShoppingCartOutlined 
                      className="text-orangicide text-2xl lg:text-3xl hover:scale-110 transition-transform cursor-pointer" 
                      onClick={openDrawer}
                    />
                  </Badge>
                </div>
              )}

              {/* Área de Login */}
              <div className='flex items-center gap-2'>
                {isLoading ? (
                  <Skeleton.Avatar active size="small" />
                ) : profile ? (
                  <UserMenu profile={profile} />
                ) : (              
                  <div className="flex items-center cursor-pointer" onClick={() => navigate('/login')}>
                    <span className="font-orator font-bold text-orangicide text-sm lg:text-lg hover:scale-110 transition-transform">LOG IN</span>
                    <div className="w-6 lg:w-8 flex items-center hover:scale-110 transition-transform ml-3 lg:ml-4">
                      <svg className="w-full h-full text-orangicide" viewBox="0 0 554.53 554.54" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0,0v554.54h554.53V0H0ZM427.53,505.34H49.19V132.6l83.41-83.41h372.74v378.33l-77.81,77.82Z"/>
                        <circle cx="277.26" cy="194.98" r="74.24"/>
                        <path d="M147.58,410.53c0-71.63,58.06-129.69,129.68-129.69s129.69,58.06,129.69,129.69c0,3.62-.15,7.2-.44,10.74-.01.19-.03.37-.04.56l-15.69,18.22h-243.2v-29.52Z"/>
                      </svg>
                    </div>
                  </div>
                )}
              </div>

              {/* Botão do menu mobile */}
              <button
                className="md:hidden text-orangicide text-2xl"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
              </button>
            </div>
          </div>
        </div>

        {/* Menu mobile */}
        {mobileMenuOpen && (
          <nav className="md:hidden font-orator font-bold text-xl flex flex-col space-y-4 pt-6">
            <NavLinks isMobile={true}/>
          </nav>
        )}
      </header>

      <main className='flex-1 pt-15'>
        <Outlet />
      </main>

      <footer className="bg-charcoal text-gray-300 font-barlow">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {/* Coluna 1: Logo */}
            <div className="flex flex-col items-center pt-6">
              <img
                src="/assets/images/ModlinkLogo.png" alt="Modlink Logo"
                className="w-30 h-auto mb-6"
                onClick={() => navigate("/")}
                style={{ cursor: "pointer" }}
              />
            </div>

            {/* Coluna 2: Quick Links */}
            <div className="ml-5 md:ml-20">
              <h3 className="font-orator font-bold text-white text-xl mb-5">QUICK LINKS</h3>
              <ul className="space-y-1">
                <li><Link to='/' onClick={() => window.scrollTo(0, 0)} className='font-orator text-gray-300 text-lg transition-colors'>Home</Link></li>
                <li><Link to='/about' onClick={() => window.scrollTo(0, 0)} className='font-orator text-gray-300 text-lg transition-colors'>About Us</Link></li>
                <li><Link to='/merch' onClick={() => window.scrollTo(0, 0)} className='font-orator text-gray-300 text-lg transition-colors'>Chains</Link></li>
                <li><Link to='/catalog' onClick={() => window.scrollTo(0, 0)} className='font-orator text-gray-300 text-lg transition-colors'>Catalog</Link></li>
                <li><Link to='/fabrication' onClick={() => window.scrollTo(0, 0)} className='font-orator text-gray-300 text-lg transition-colors'>Fabrication</Link></li>
                {profile?.profile === 'admin' && (
                  <li><Link to='/merch' onClick={() => window.scrollTo(0, 0)} className='font-orator text-gray-300 text-lg transition-colors'>Shop</Link></li>
                )}
              </ul>
            </div>

            {/* Coluna 4: Contact Us */}
            <div className="ml-5 md:ml-10">
              <h3 className="font-orator font-bold text-white text-xl mb-5">CONTACT US</h3>
              <ul className="space-y-5">
                <li className="flex items-start">
                  <img src="/assets/icons/LocationIcon.svg" alt="Location Icon" className="h-9 mr-2 pt-3 icon-orangicide" />
                  <span className="font-orator text-base text-gray-300">PO Box 1921<br />Truckee, CA, USA 96160</span>
                </li>
                <li className="flex items-center">
                  <img src="/assets/icons/PhoneIcon.svg" alt="Phone icon" className="h-7 mr-2 pb-1 icon-orangicide" />
                  <a href="tel:+18663085465" className="font-orator text-base text-gray-300 transition-colors">866.308.5465</a>
                </li>
                <li className="flex items-center">
                  <img src="/assets/icons/MailIcon.svg" alt="Mail icon" className="h-8 mr-2 py-1 icon-orangicide"/>
                  <a href="mailto:info@modlink.com" className="font-orator text-base text-gray-300 transition-colors">info@modlink.com</a>
                </li>
              </ul>
            </div>
          </div>

          <hr className="border-gray-500 my-8 md:my-10" />

          <div className="flex flex-col items-center">
            <p className="font-orator text-gray-400 text-base text-center mb-4">
              © {new Date().getFullYear()} Mod-Link LLC. All rights reserved.
            </p>
            {/* <div className="flex justify-center space-x-4 md:space-x-6">
              <Link to='/' onClick={() => window.scrollTo(0, 0)} className='text-gray-400 hover:text-orangicide text-sm transition-colors'>Privacy Policy</Link>
              <Link to='/' onClick={() => window.scrollTo(0, 0)} className='text-gray-400 hover:text-orangicide text-sm transition-colors'>Terms of Service</Link>
              <Link to='/' onClick={() => window.scrollTo(0, 0)} className='text-gray-400 hover:text-orangicide text-sm transition-colors'>Sitemap</Link>
            </div> */}
          </div>
        </div>
      </footer>
    </div>
  );
};
